<template>
    <page :busy="busy">
        <b-container fluid style="height: 100%">
            <h5 class="my-2">Office Hours</h5>
            <p>
                Let guests know when you will be available to chat. After hours, guests will see a message letting them
                know that chat availability is limited. That message can be edited below.
            </p>
            <form-textarea
                label="Outside Office Hours Message"
                placeholder="Enter message here"
                v-model="message"
                maxlength="255"
                @input="debouncedUpdate"
            />

            <div style="overflow-x: scroll; height: 100%">
                <table class="table" style="min-width: 800px">
                    <thead>
                        <tr>
                            <th>Period</th>
                            <th>Start Time</th>
                            <th>End Time</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="officeHour in officeHours">
                            <td v-if="isEditId == officeHour.id">
                                <form-dropdown class="mb-0" v-model="officeHour.period" :options="periodListDropdown" />
                            </td>
                            <td v-else>{{ periodDropdownLabel(officeHour.period) }}</td>
                            <td v-if="isEditId == officeHour.id">
                                <form-dropdown
                                    class="mb-0"
                                    v-model="officeHour.start"
                                    :options="startTimeListDropdown"
                                />
                            </td>
                            <td v-else>{{ formattedOfficeHours(officeHour.start) }}</td>
                            <td v-if="isEditId == officeHour.id">
                                <form-dropdown class="mb-0" v-model="officeHour.end" :options="endTimeListDropdown" />
                            </td>
                            <td v-else>{{ formattedOfficeHours(officeHour.end) }}</td>
                            <td v-if="isEditId == officeHour.id" style="display: flex; flex-direction: row">
                                <b-button class="mx-1" @click="update(officeHour)">Save</b-button>
                                <b-button class="mx-1" @click="cancelEdit()">Cancel</b-button>
                            </td>
                            <td v-else style="display: flex; flex-direction: row">
                                <b-button class="mx-1" @click="(isEditId = officeHour.id), (isCreateNew = false)"
                                    >Edit</b-button
                                >
                                <b-button class="mx-1" @click="removeHours(officeHour)">Delete</b-button>
                            </td>
                        </tr>
                        <tr v-if="isCreateNew">
                            <td>
                                <form-dropdown
                                    class="mb-0"
                                    v-model="tmpOfficeHour.period"
                                    :options="periodListDropdown"
                                />
                            </td>
                            <td>
                                <form-dropdown
                                    class="mb-0"
                                    v-model="tmpOfficeHour.start"
                                    :options="startTimeListDropdown"
                                />
                            </td>
                            <td>
                                <form-dropdown
                                    class="mb-0"
                                    v-model="tmpOfficeHour.end"
                                    :options="endTimeListDropdown"
                                />
                            </td>
                            <td style="display: flex; flex-direction: row">
                                <b-button class="mx-1" @click="create()">Save</b-button>
                                <b-button class="mx-1" @click="cancelAdd()">Cancel</b-button>
                            </td>
                        </tr>
                        <tr v-else>
                            <b-button variant="primary" class="mx-1 my-2" @click="addNew()">+ Add Hours</b-button>
                        </tr>
                    </tbody>
                </table>
            </div>
        </b-container>
    </page>
</template>

<script>
    import api from '../../../api';
    import { mapGetters } from 'vuex';
    import format from 'date-fns/format';
    import { formatResponseError } from '../../../utils/api';
    import { CHAT_SETTINGS_OFFICE_HOURS_ENDPOINT } from '../../../endpoints';
    import FormSwitch from '../../generic/form-switch/form-switch.vue';
    import FormTextarea from '../../generic/form-textarea/form-textarea.vue';
    import formDropdown from '../../generic/form-dropdown/form-dropdown.vue';
    import Page from '../../generic/page/page.vue';
    import _ from 'lodash';
    export default {
        components: {
            FormSwitch,
            Page,
            FormTextarea,
            formDropdown,
        },
        data() {
            return {
                busy: false,
                officeHours: null,
                isEditId: null,
                isCreateNew: false,
                message: '',
                tmpOfficeHour: {
                    period: 'WEEKDAYS',
                    start: '09:00:00',
                    end: '17:00:00',
                },
                timeList: [],
                periodList: [],
            };
        },
        mounted() {
            this.fetchAllData();
        },
        computed: {
            ...mapGetters('header', ['userSubscriberId']),
            startTimeListDropdown() {
                const list = this.timeList.map((item) => {
                    return {
                        text: item.text,
                        value: item.value,
                        click: () => {
                            if (this.isCreateNew) {
                                this.tmpOfficeHour.start = item.value;
                            } else {
                                this.officeHours.find((officeHour) => officeHour.id === this.isEditId).start =
                                    item.value;
                            }
                        },
                    };
                });
                return list;
            },
            endTimeListDropdown() {
                const list = this.timeList.map((item) => {
                    return {
                        text: item.text,
                        value: item.value,
                        click: () => {
                            if (this.isCreateNew) {
                                this.tmpOfficeHour.end = item.value;
                            } else {
                                this.officeHours.find((officeHour) => officeHour.id === this.isEditId).end = item.value;
                            }
                        },
                    };
                });
                return list;
            },
            periodListDropdown() {
                const list = this.periodList.map((item) => {
                    return {
                        text: item.text,
                        value: item.value,
                        click: () => {
                            if (this.isCreateNew) {
                                this.tmpOfficeHour.period = item.value;
                            } else {
                                this.officeHours.find((officeHour) => officeHour.id === this.isEditId).period =
                                    item.value;
                            }
                        },
                    };
                });
                return list;
            },
        },
        methods: {
            async cancelEdit() {
                this.isEditId = null;
                await this.fetchOfficeHours();
            },
            async cancelAdd() {
                this.isCreateNew = false;
                await this.fetchOfficeHours();
            },
            addNew() {
                this.isEditId = null;
                this.isCreateNew = true;
                this.tmpOfficeHour = {
                    period: 'WEEKDAYS',
                    start: '08:00:00',
                    end: '17:00:00',
                };
            },
            periodDropdownLabel(period) {
                return this.periodList.find((item) => item.value === period)?.text || 'N/A';
            },
            formattedOfficeHours(time) {
                const dummyDate = '1970-01-01T';
                return format(new Date(dummyDate + time), 'hh:mm a');
            },
            async create() {
                try {
                    this.busy = true;
                    const { data } = await api.post(CHAT_SETTINGS_OFFICE_HOURS_ENDPOINT(), {
                        period: this.tmpOfficeHour.period,
                        start: this.tmpOfficeHour.start,
                        end: this.tmpOfficeHour.end,
                    });
                    await this.fetchOfficeHours();
                    this.isCreateNew = false;
                    this.successToast({ text: 'Office hours successfully created' });
                } catch (e) {
                    let errorMessage = '';
                    if (e.response && e.response.data) {
                        errorMessage = e.response.data;
                    }
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: errorMessage || generic || 'Could not create office hours' });
                } finally {
                    this.busy = false;
                }
            },
            async update(officeHour) {
                try {
                    this.busy = true;
                    const { data } = await api.put(CHAT_SETTINGS_OFFICE_HOURS_ENDPOINT() + `${officeHour.id}/`, {
                        period: officeHour.period,
                        start: officeHour.start,
                        end: officeHour.end,
                    });
                    await this.fetchOfficeHours();
                    this.isEditId = null;
                    this.successToast({ text: 'Office hours successfully updated' });
                } catch (e) {
                    let errorMessage = '';
                    if (e.response && e.response.data) {
                        errorMessage = e.response.data;
                    }
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: errorMessage || generic || 'Could not update office hours' });
                } finally {
                    this.busy = false;
                }
            },
            async removeHours(officeHour) {
                try {
                    this.busy = true;
                    const { data } = await api.delete(CHAT_SETTINGS_OFFICE_HOURS_ENDPOINT() + `${officeHour.id}/`);
                    await this.fetchOfficeHours();
                    this.successToast({ text: 'Office hours successfully removed' });
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not remove office hours' });
                } finally {
                    this.busy = false;
                }
            },
            async fetchAllData() {
                this.busy = true;
                await this.fetchPeriods();
                await this.fetchHours();
                await this.fetchOfficeHours();
                this.busy = false;
            },
            async fetchOfficeHours() {
                try {
                    this.busy = true;
                    let { data } = await api.get(CHAT_SETTINGS_OFFICE_HOURS_ENDPOINT());
                    this.officeHours = data;
                    data = (await api.get(CHAT_SETTINGS_OFFICE_HOURS_ENDPOINT() + `message/`)).data;
                    this.message = data.message;
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not load office hours' });
                } finally {
                    this.busy = false;
                }
            },
            async fetchPeriods() {
                try {
                    this.busy = true;
                    const { data } = await api.get(CHAT_SETTINGS_OFFICE_HOURS_ENDPOINT() + `periods/`);
                    this.periodList = data.map((item) => {
                        return {
                            text: item
                                .split(' ')
                                .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                                .join(' '),
                            value: item,
                        };
                    });
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not load periods' });
                } finally {
                    this.busy = false;
                }
            },
            async fetchHours() {
                try {
                    this.busy = true;
                    const { data } = await api.get(CHAT_SETTINGS_OFFICE_HOURS_ENDPOINT() + `hours/`);
                    this.timeList = data.map((item) => {
                        const formattedTime = format(new Date(`1970-01-01T${item}`), 'hh:mm a');
                        return {
                            text: formattedTime,
                            value: item,
                        };
                    });
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not load office hours' });
                } finally {
                    this.busy = false;
                }
            },
            debouncedUpdate: _.debounce(async function () {
                await this.updateMessage();
            }, 1000),
            async updateMessage() {
                try {
                    this.busy = true;
                    await api.post(CHAT_SETTINGS_OFFICE_HOURS_ENDPOINT() + `update-message/`, {
                        message: this.message,
                    });
                    this.successToast({ text: 'Message successfully updated' });
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not update message' });
                } finally {
                    this.busy = false;
                }
            },
        },
    };
</script>

<style scoped lang="scss">
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';
    :deep(*) {
        font-family: 'Lato', sans-serif !important;
    }
    td {
        vertical-align: middle !important;
    }
</style>
